/**
 * Team Order Savings - Simplified Version
 * Shows discount message based on product page quantity selector
 */

class TeamOrderSavings {
  constructor() {
    this.b2bTiers = [];
    this.currentTier = null;
    this.nextTier = null;
    this.currentQuantity = 0;
    this.productFormIds = [];
    this.currentProductHandle = null;
    this.currentProductId = null;
    this.cartProductCount = 0;
    this.lastCartToken = null;
    this.init();
  }

  init() {
    const savingsWrapper = document.querySelector('.team-order-savings-wrapper');
    if (!savingsWrapper) {
      return;
    }

    console.log('Team Order Savings: Initializing...');

    this.detectCurrentProduct();
    this.collectProductFormIds();

    this.waitForB2BTable(() => {
      this.loadB2BTiers();
      this.setupListeners();
      this.fetchCartProductCount(() => {
        this.updateDisplay();
      });
    });
  }

  detectCurrentProduct() {
    const pathMatch = window.location.pathname.match(/\/products\/([^/?]+)/);
    if (pathMatch && pathMatch[1]) {
      this.currentProductHandle = pathMatch[1];
    }

    const sprEl = document.getElementById('shopify-product-reviews');
    if (sprEl && sprEl.getAttribute('data-id')) {
      const pid = parseInt(sprEl.getAttribute('data-id'));
      if (pid && !isNaN(pid)) this.currentProductId = pid;
    }

    if (!this.currentProductId) {
      const jsonScripts = document.querySelectorAll('script[id^="ProductJSON-"]');
      if (jsonScripts.length > 0) {
        const match = jsonScripts[0].id.match(/ProductJSON-(\d+)/);
        if (match && match[1]) {
          const pid = parseInt(match[1]);
          if (pid && !isNaN(pid)) this.currentProductId = pid;
        }
      }
    }

    if (typeof window.globalProductId !== 'undefined' && window.globalProductId && !this.currentProductId) {
      const pid = parseInt(window.globalProductId);
      if (pid && !isNaN(pid)) this.currentProductId = pid;
    }

    console.log('Detected product — handle:', this.currentProductHandle, 'id:', this.currentProductId);
  }

  collectProductFormIds() {
    const forms = document.querySelectorAll('form.product-form, form[action*="/cart/add"]');
    forms.forEach((form) => {
      if (form.id) this.productFormIds.push(form.id);
    });
    console.log('Product form IDs found:', this.productFormIds);
  }

  waitForB2BTable(callback, attempts = 0) {
    const maxAttempts = 30;
    const b2bTable = document.querySelector('.bss-b2b-qty-table-row');
    
    if (b2bTable) {
      console.log('B2B table found');
      callback();
    } else if (attempts < maxAttempts) {
      setTimeout(() => this.waitForB2BTable(callback, attempts + 1), 200);
    } else {
      console.warn('B2B table not found, using default tiers');
      this.useDefaultTiers();
      callback();
    }
  }

  loadB2BTiers() {
    const rows = document.querySelectorAll('.bss-b2b-qty-table-row');
    
    if (rows.length === 0) {
      this.useDefaultTiers();
      return;
    }

    this.b2bTiers = [];
    rows.forEach((row, index) => {
      const dataMin = row.getAttribute('data-min');
      const dataMax = row.getAttribute('data-max');
      
      if (dataMin != null && dataMin !== '') {
        const discountCell = row.querySelector('td:last-child');
        let discount = 0;
        
        if (discountCell) {
          const match = discountCell.textContent.match(/(\d+)/);
          if (match) discount = parseInt(match[1]);
        }

        const minVal = parseInt(dataMin);
        let maxVal = parseInt(dataMax);
        let isOpenEnded = false;

        if (dataMax == null || dataMax === '' || isNaN(maxVal)) {
          maxVal = 999999;
          isOpenEnded = true;
        } else if (maxVal === minVal && index === rows.length - 1) {
          maxVal = 999999;
          isOpenEnded = true;
        }

        this.b2bTiers.push({
          index,
          min: minVal,
          max: maxVal,
          discount,
          isOpenEnded,
          element: row
        });
      }
    });

    this.b2bTiers.sort((a, b) => a.min - b.min);
    console.log('Loaded tiers:', this.b2bTiers);
  }

  useDefaultTiers() {
    this.b2bTiers = [
      { index: 0, min: 7, max: 12, discount: 15, isOpenEnded: false },
      { index: 1, min: 13, max: 26, discount: 20, isOpenEnded: false },
      { index: 2, min: 27, max: 50, discount: 25, isOpenEnded: false },
      { index: 3, min: 51, max: 99, discount: 30, isOpenEnded: false },
      { index: 4, min: 100, max: 999999, discount: 35, isOpenEnded: true }
    ];
  }

  fetchCartProductCount(callback) {
    if (!this.currentProductHandle && !this.currentProductId) {
      this.cartProductCount = 0;
      if (callback) callback();
      return;
    }

    fetch('/cart.js', {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin'
    })
      .then((res) => {
        if (!res.ok) throw new Error('Cart fetch failed');
        return res.json();
      })
      .then((cart) => {
        if (!cart || !Array.isArray(cart.items)) {
          this.cartProductCount = 0;
          if (callback) callback();
          return;
        }

        if (cart.token) {
          this.lastCartToken = cart.token;
        }

        let count = 0;
        cart.items.forEach((item) => {
          let matches = false;

          if (this.currentProductHandle && item.handle === this.currentProductHandle) {
            matches = true;
          }

          if (!matches && this.currentProductId && item.product_id === this.currentProductId) {
            matches = true;
          }

          if (matches) {
            count += item.quantity || 0;
          }
        });

        this.cartProductCount = count;
        console.log(`Cart — ${this.currentProductHandle || 'product'}: ${this.cartProductCount} units already in cart (out of ${cart.item_count} total)`);
        if (callback) callback();
      })
      .catch((err) => {
        console.warn('Failed to fetch cart for product count — falling back to 0:', err);
        this.cartProductCount = 0;
        if (callback) callback();
      });
  }

  setupListeners() {
    document.addEventListener('change', (e) => {
      if (e.target.matches('input[name="quantity"], .quantity__input')) {
        if (!this.isProductQuantityInput(e.target)) return;
        console.log('Quantity changed (delegated)');
        setTimeout(() => this.updateDisplay(), 100);
      }
    });

    document.addEventListener('input', (e) => {
      if (e.target.matches('input[name="quantity"], .quantity__input')) {
        if (!this.isProductQuantityInput(e.target)) return;
        setTimeout(() => this.updateDisplay(), 100);
      }
    });

    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.quantity__button, button[name="plus"], button[name="minus"]');
      if (btn) {
        const container = btn.closest('quantity-input, .quantity');
        if (container) {
          const input = container.querySelector('input[name="quantity"], .quantity__input');
          if (input && this.isProductQuantityInput(input)) {
            console.log('Quantity button clicked (delegated)');
            setTimeout(() => this.updateDisplay(), 150);
          }
        }
      }
    });

    const observer = new MutationObserver(() => {
      setTimeout(() => this.updateDisplay(), 100);
    });
    
    document.querySelectorAll('.bss-b2b-qty-table-row').forEach(row => {
      observer.observe(row, { attributes: true, attributeFilter: ['class'] });
    });

    document.addEventListener('cart:add', () => {
      this.fetchCartProductCount(() => this.updateDisplay());
    });
    document.addEventListener('cart:update', () => {
      this.fetchCartProductCount(() => this.updateDisplay());
    });
    document.addEventListener('ajaxCart.afterCartLoad', () => {
      this.fetchCartProductCount(() => this.updateDisplay());
    });

    setInterval(() => {
      this.fetchCartProductCount(() => this.updateDisplay());
    }, 5000);
  }

  isProductQuantityInput(input) {
    let ancestor = input.parentElement;
    let depth = 0;
    while (ancestor && depth < 20) {
      if (ancestor.classList) {
        if (
          ancestor.classList.contains('product__sticky_add__form') ||
          ancestor.classList.contains('product-sticky')
        ) {
          return false;
        }
      }
      if (ancestor.id && ancestor.id === 'ProductSticky') {
        return false;
      }
      ancestor = ancestor.parentElement;
      depth++;
    }

    const formId = input.getAttribute('form');
    if (formId && this.productFormIds.includes(formId)) {
      return true;
    }

    if (formId && formId.startsWith('product-sticky')) {
      return false;
    }

    ancestor = input.parentElement;
    depth = 0;
    while (ancestor && depth < 20) {
      if (ancestor.tagName === 'FORM' && ancestor.id && this.productFormIds.includes(ancestor.id)) {
        return true;
      }
      if (ancestor.classList && (
        ancestor.classList.contains('product-form') ||
        ancestor.classList.contains('product') ||
        ancestor.classList.contains('product__info-wrapper') ||
        ancestor.classList.contains('product__info-container')
      )) {
        return true;
      }
      if (ancestor.id && ancestor.id.includes('product-form')) {
        return true;
      }
      if (ancestor.getAttribute && ancestor.getAttribute('data-section-type') === 'main-product') {
        return true;
      }
      ancestor = ancestor.parentElement;
      depth++;
    }
    return false;
  }

  getCurrentQuantity() {
    let pageQuantity = 0;
    let cartQuantity = this.cartProductCount;

    const seenInputs = new Set();
    const allInputs = [];

    document.querySelectorAll('input[name="quantity"]').forEach(input => {
      if (!seenInputs.has(input)) {
        seenInputs.add(input);
        if (this.isProductQuantityInput(input)) {
          allInputs.push(input);
        }
      }
    });

    document.querySelectorAll('.quantity__input').forEach(input => {
      if (!seenInputs.has(input)) {
        seenInputs.add(input);
        if (this.isProductQuantityInput(input)) {
          allInputs.push(input);
        }
      }
    });

    if (allInputs.length > 0) {
      allInputs.forEach(input => {
        const qty = parseInt(input.value) || 0;
        pageQuantity += qty;
      });
    }

    const totalQuantity = pageQuantity + cartQuantity;
    //console.log(`Quantity: Page(${this.currentProductHandle})=${pageQuantity} + Cart(${this.currentProductHandle})=${cartQuantity} = Total=${totalQuantity}`);
    
    return totalQuantity > 0 ? totalQuantity : 0;
  }

  findCurrentTier(quantity) {
    return this.b2bTiers.find(t => quantity >= t.min && quantity <= t.max) || null;
  }

  findNextTier(quantity) {
    return this.b2bTiers.find(t => quantity < t.min) || null;
  }

  updateDisplay() {
    const quantity = this.getCurrentQuantity();
    
    const titleEl = document.querySelector('.savings-title');
    const descEl = document.querySelector('.savings-description');

    if (!titleEl || !descEl) return;

    if (this.b2bTiers.length === 0) {
      return;
    }

    if (quantity === 0) {
      const firstTier = this.b2bTiers[0];
      if (firstTier) {
        titleEl.textContent = 'TEAM ORDER SAVINGS';
        descEl.textContent = `Order ${firstTier.min}+ eligible items and save ${firstTier.discount}% on your order. Mix & match eligible jerseys, jackets & more — total quantity counts toward your discount.`;
      }
      return;
    }

    this.currentTier = this.findCurrentTier(quantity);
    this.nextTier = this.findNextTier(quantity);

    //console.log('Quantity:', quantity, 'Current Tier:', this.currentTier, 'Next:', this.nextTier);

    if (this.currentTier) {
      if (this.nextTier) {
        const itemsNeeded = this.nextTier.min - quantity;
        titleEl.textContent = `SAVING ${this.currentTier.discount}% NOW!`;
        descEl.textContent = `You're currently saving ${this.currentTier.discount}%! Add ${itemsNeeded} more items to unlock ${this.nextTier.discount}% off (${this.nextTier.min}${this.nextTier.isOpenEnded ? '+' : '-' + this.nextTier.max} items).`;
      } else {
        titleEl.textContent = '🎉 MAXIMUM SAVINGS UNLOCKED!';
        descEl.textContent = `Congratulations! You're saving ${this.currentTier.discount}% on your order!`;
      }
    } else if (this.nextTier && quantity < this.nextTier.min) {
      const itemsNeeded = this.nextTier.min - quantity;
      titleEl.textContent = 'TEAM ORDER SAVINGS';
      if (itemsNeeded <= 10) {
        descEl.textContent = `Order ${this.nextTier.min}+ eligible items and save ${this.nextTier.discount}% on your order. Add ${itemsNeeded} more to unlock your discount!`;
      } else {
        descEl.textContent = `Order ${this.nextTier.min}+ eligible items and save ${this.nextTier.discount}% on your order. Mix & match eligible jerseys, jackets & more — total quantity counts toward your discount.`;
      }
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.teamOrderSavings = new TeamOrderSavings();
  });
} else {
  window.teamOrderSavings = new TeamOrderSavings();
}

window.TeamOrderSavingsDebug = {
  refresh() {
    if (window.teamOrderSavings) {
      window.teamOrderSavings.fetchCartProductCount(() => {
        window.teamOrderSavings.updateDisplay();
      });
    }
  },
  info() {
    if (window.teamOrderSavings) {
      const t = window.teamOrderSavings;
      // console.log('=== TeamOrderSavings Debug Info ===');
      // console.log('Product Handle:', t.currentProductHandle);
      // console.log('Product ID:', t.currentProductId);
      // console.log('Cart Product Count (this product only):', t.cartProductCount);
      // console.log('Tiers:', t.b2bTiers);
      // console.log('Current Calculated Quantity:', t.getCurrentQuantity());
    }
  }
};
